"use client";
import React from 'react';
import MainLayout from '@/layouts/MainLayout';
import Image from 'next/image';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import CallToAction from '@/components/CallToAction';

// Highlighted text for manifesto
const HighlightedText = ({ children, color }: { children: React.ReactNode, color: string }) => (
  <span className={`inline-block rounded-lg px-2 py-1 mx-1 my-1 ${color} text-black font-semibold transition-all duration-300`}>{children}</span>
);

const AboutPage: React.FC = () => {
  return (
    <MainLayout>
      <div className="relative py-16 min-h-screen flex flex-col items-center justify-center overflow-hidden">
        <div className="relative z-10 w-full max-w-4xl mx-auto flex flex-col gap-16">
          {/* Artistic Hero/Manifesto */}
          <section className="rounded-3xl border border-border bg-card shadow-xl px-8 py-14 flex flex-col gap-8 items-center text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-foreground leading-tight mb-4">
              We believe in <HighlightedText color="bg-green-400">creators</HighlightedText>.<br />
              Not content machines.
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-6">
              Saved is for people who want to <span className="font-semibold text-foreground">share</span>, <span className="font-semibold text-foreground">inspire</span>, and <span className="font-semibold text-foreground">build community</span>—not just churn out noise.
            </p>
            <Accordion type="single" collapsible className="w-full max-w-2xl mx-auto text-left">
              <AccordionItem value="item-1">
                <AccordionTrigger className="text-foreground">Why UGC matters</AccordionTrigger>
                <AccordionContent>
                  <span className="text-gray-400">
                    Stories connect us. UGC is the new campfire. We make it easy for anyone to share, remix, and shine.
                  </span>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger className="text-foreground">Not just another AI tool</AccordionTrigger>
                <AccordionContent>
                  <span className="text-muted-foreground">
                    We use AI to <HighlightedText color="bg-blue-200">empower</HighlightedText>, not replace. No soulless slop—just tools that help you create what you're proud of.
                  </span>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3">
                <AccordionTrigger className="text-foreground">Marketplace for real value</AccordionTrigger>
                <AccordionContent>
                  <span className="text-muted-foreground">
                    We're building a marketplace where creators, brands, and fans all win. Your work, your way, your rewards.
                  </span>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4">
                <AccordionTrigger className="text-foreground">A product you'll love</AccordionTrigger>
                <AccordionContent>
                  <span className="text-muted-foreground">
                    We obsess over making Saved delightful, useful, and genuinely valuable. If it's not fun or helpful, we don't ship it.
                  </span>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            <div className="mt-8 text-2xl sm:text-3xl font-bold text-foreground">
              Join us. <HighlightedText color="bg-green-400">Share your story</HighlightedText>.<br />
              <span className="text-muted-foreground text-xl font-medium">Help us build the future of UGC.</span>
            </div>
          </section>

          {/* Founders Artistic Card */}
          <section className="relative rounded-2xl border border-border bg-card shadow-lg px-8 py-10 flex flex-col items-center gap-6">
            <div className="absolute -top-10 left-1/2 -translate-x-1/2 flex -space-x-4 z-10">
              <Image src="/team/aayush.jpg" alt="Aayush Gupta" width={64} height={64} className="rounded-full border-4 border-border shadow-lg" />
              <Image src="/team/akash.jpg" alt="Akash Moorching" width={64} height={64} className="rounded-full border-4 border-border shadow-lg" />
              <Image src="/team/eric.jpg" alt="Eric Chen" width={64} height={64} className="rounded-full border-4 border-border shadow-lg" />
            </div>
            <div className="mt-10 text-center">
              <p className="text-foreground text-xl font-semibold">Aayush, Akash, & Eric</p>
              <p className="text-muted-foreground text-sm mb-4">Co-founders</p>
            </div>
            <div className="mt-4">
              <a href="https://discord.gg/MqdzdRr6DP" target="_blank" rel="noopener noreferrer" aria-label="Join our Discord">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="bg-white text-black px-5 py-2 rounded-lg font-bold shadow-md hover:bg-green-100 transition-all border-2 border-green-400 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 127.14 96.36" fill="#000000">
                          <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
                        </svg>
                        Reach us here
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Connect with the founders & community</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </a>
            </div>
          </section>
        </div>
      </div>
    </MainLayout>
  );
};

export default AboutPage;
